import {
  Table,
  TableBody,
  TableRow,
  IconButton,
  Paper,
  Box,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import * as styles from './styles';
import * as localState from '../../state';
import * as state from '@/components/Analysis/state';
import { formatDefectValue } from '../../utils';
import { TOTAL_AREA_FIELD_FORMATTER } from '../../constants';
import { LocationTag, NOT_APPLICABLE_LABEL } from '@/constants';
import { useIsolateCustomBlock } from '../../hooks';
import {
  PointOfInterestTemplateTypeEnum,
  UnitSystemEnum,
  usePaintBlockPointsLazyQuery,
} from '@/__generated__/graphql';
import { SQFEET_TO_SQMETERS_FACTOR } from '@/utils/unitSystem';
import { CreatePaintBlockReportModal } from '@/components/Analysis/Insights/InsightsExplorer/PaintBlock/CreatePaintBlockReportModal';
import { useState } from 'react';
import { FileUploadAndViewer } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileUploadAndViewer';
import { useSaveIsolateCustomBlockScreenshot } from '../../hooks/useSaveIsolateCustomBlockScreenshot';
import { AnalysisCustomBlockWithFiles } from '@/types';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import { useWorkScopeFlow } from '../../hooks/useWorkScopeFlow';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useFeatureFlag } from '@/hooks';
import { useAtom, useAtomValue } from 'jotai';

const GENERAL_FIELDS = ['area', 'Total Corroded Area', 'Ri4+Ri5'];

const DEFECT_FIELD = ['Ri0', 'Ri1', 'Ri2', 'Ri3', 'Ri4', 'Ri5'];

export const SelectedCustomBlockPanel = () => {
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);

  const [selectedCustomBlock, setSelectedCustomBlock] = useAtom(localState.selectedCustomBlock);
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const { showSnackBar } = useSnackBarMessage({});
  const { startAddingWorkScope, isLoading: isWorkScopeLoading } = useWorkScopeFlow();
  const { deSelectCustomBlock } = useIsolateCustomBlock({});
  const { uploadCuboidScreenshot, loading } =
    useSaveIsolateCustomBlockScreenshot(selectedCustomBlock);
  const unitSystem = useAtomValue(state.unitSystem);

  const [fetchPaintRegionPoints] = usePaintBlockPointsLazyQuery();

  const workScopeTemplateExists = allPointOfInterestTemplates.some(
    ({ type }) => type === PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
  );

  const isAlphaUser = useFeatureFlag('alpha-user');

  const unitSuffix = unitSystem === UnitSystemEnum.Imperial ? 'ft²' : 'm²';

  // Since data in the DB, was calculated in metre square, we need to convert it to feet square
  const multiplicationFactor =
    unitSystem === UnitSystemEnum.Imperial ? SQFEET_TO_SQMETERS_FACTOR : 1;

  const onIconButtonClickHandler = async () => {
    await startAddingWorkScope({
      onError: (reason) => {
        showSnackBar(reason, 'error');
      },
    });
  };

  if (!selectedCustomBlock) {
    return (
      <Box>
        <Typography variant="h6">No block selected</Typography>
      </Box>
    );
  }

  const generalData = GENERAL_FIELDS.map((field) => {
    const foundField = selectedCustomBlock?.totalArea?.find((item) => item?.type === field);
    if (!foundField) {
      return undefined;
    }

    const formattedValue = formatDefectValue(foundField.value);
    const convertedValue =
      formattedValue === undefined ? formattedValue : formattedValue * multiplicationFactor;

    return {
      label: TOTAL_AREA_FIELD_FORMATTER[field] || field,
      value: convertedValue,
    };
  }).filter(Boolean);

  const defectsData = DEFECT_FIELD.map((field) => {
    const foundField = selectedCustomBlock?.totalArea?.find((item) => item?.type === field);
    if (!foundField) {
      return undefined;
    }

    const formattedValue = formatDefectValue(foundField.value);
    const convertedValue =
      formattedValue === undefined ? formattedValue : formattedValue * multiplicationFactor;
    return {
      label: TOTAL_AREA_FIELD_FORMATTER[field] || field,
      value: convertedValue,
    };
  }).filter(Boolean);

  const captureViewClickHandler = async () => {
    await uploadCuboidScreenshot();
    const response = await fetchPaintRegionPoints({
      variables: {
        paintRegionId: selectedCustomBlock.paintRegionId,
      },
      fetchPolicy: 'network-only',
    });

    const files = response.data?.paintRegion?.files;
    if (files) {
      setSelectedCustomBlock(
        (prev) =>
          ({
            ...prev,
            files,
          } as AnalysisCustomBlockWithFiles)
      );
    }
  };

  return (
    <styles.Container>
      <styles.Header onClick={deSelectCustomBlock}>
        <IconButton size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <styles.BackText>Back to all</styles.BackText>
      </styles.Header>

      <styles.TitleContainer>
        <styles.BlockTitle>{selectedCustomBlock?.name || NOT_APPLICABLE_LABEL}</styles.BlockTitle>

        {isAlphaUser && (
          <Tooltip
            title={workScopeTemplateExists ? 'Add Work Scope' : 'Work Scope template not found'}
          >
            <IconButton
              size="small"
              onClick={onIconButtonClickHandler}
              disabled={isWorkScopeLoading || !workScopeTemplateExists}
            >
              <CenterFocusWeakIcon />
            </IconButton>
          </Tooltip>
        )}

        <IconButton size="small">
          <MoreHorizIcon />
        </IconButton>
      </styles.TitleContainer>

      <styles.SectionContainer mt={2}>
        <styles.SectionTitle>General</styles.SectionTitle>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableBody>
              {generalData.map((item) => (
                <TableRow
                  key={item?.label}
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                  }}
                >
                  <styles.StyledTableCellLabel>{item?.label}</styles.StyledTableCellLabel>
                  <styles.StyledTableCellValue>
                    {item?.value?.toFixed(2)}
                    {unitSuffix}
                  </styles.StyledTableCellValue>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </styles.SectionContainer>

      <styles.SectionContainer>
        <styles.SectionTitle>Coating breakdown</styles.SectionTitle>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableBody>
              {defectsData.map((item) => (
                <TableRow
                  key={item?.label}
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                    display: 'flex',
                  }}
                >
                  <styles.StyledTableCellLabel>{item?.label}</styles.StyledTableCellLabel>
                  <styles.StyledTableCellValue sx={{ ml: 'auto' }}>
                    {item?.value?.toFixed(2)}
                    {unitSuffix}
                  </styles.StyledTableCellValue>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </styles.SectionContainer>
      <styles.SectionContainer>
        <styles.SectionTitle>Attachments</styles.SectionTitle>
        <FileUploadAndViewer
          referenceId={selectedCustomBlock.paintRegionId}
          collectionName={'paintRegion'}
          files={selectedCustomBlock?.files || []}
          onCaptureClick={captureViewClickHandler}
          loading={loading}
          location={LocationTag.ViewerSelectedCustomBlock}
        />
      </styles.SectionContainer>
      <Button
        variant="contained"
        size="large"
        sx={{ mt: 'auto', mb: 1, mx: 1 }}
        onClick={() => {
          setIsReportsModalOpen(true);
        }}
      >
        Create Report
      </Button>

      <CreatePaintBlockReportModal
        onClose={() => setIsReportsModalOpen(false)}
        open={isReportsModalOpen}
        paintRegionId={selectedCustomBlock.paintRegionId}
      />
    </styles.Container>
  );
};
